

export interface GeoLocation {
  lat: number;
  lng: number;
}

export enum ThreatLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL'
}

export interface Incident {
  id: string;
  type: string;
  description: string;
  summary?: string; // Added summary field
  location: GeoLocation;
  timestamp: number;
  threatLevel: ThreatLevel;
  source: 'RADIO' | 'DISPATCH' | 'USER_REPORT' | 'CRIME_RADAR';
  distance?: number; // In miles
  freshnessMinutes?: number; // How many minutes ago the incident was reported
  isConfirmed?: boolean; // Added for un-RED ALerTS tab
}

export interface Cluster {
  id: string; // Unique ID for the cluster
  count: number; // Number of incidents in the cluster
  x: number; // X coordinate for rendering
  y: number; // Y coordinate for rendering
  threatLevel: ThreatLevel; // Highest threat level within the cluster
}
export interface AlertLog {
  id: string;
  alertId: string;
  locationAcquired?: boolean;
  location?: string;
  reportedBy?: string;
  mapPlacement?: boolean;
  mapLocation?: string;
  alertType?: string;
  userInteraction?: boolean;
  communitySafety?: boolean;
  timestamp: string;
}

export interface AppState {
  userLocation: GeoLocation | null;
  incidents: Incident[];
  isSubscribed: boolean;
  isScanning: boolean;
  lastUpdate: number;
}

// ── GAME TYPES ────────────────────────────────────────────────────────────────

export type GameRole = 'OFFICER' | 'PARAMEDIC' | 'FIREFIGHTER' | 'OBSERVER' | 'CRIMINAL';

export interface GameRound {
  id: string;
  incidentId: string;
  incidentType: string;
  incidentDescription: string;
  incidentLocation: GeoLocation;
  targetRole: GameRole | 'ALL';
  question: string;
  choices: [string, string, string, string];
  correctIndex: number;
  explanation: string;
  startTime: number;
  endTime: number;
  status: 'GENERATING' | 'ACTIVE' | 'ENDED';
  jackpot: boolean;
  pointsBase: number;
}

export interface GameAnswer {
  id?: string;
  roundId: string;
  userId: string;
  displayName: string;
  photoURL?: string;
  choiceIndex: number;
  isCorrect: boolean;
  responseTimeMs: number;
  pointsEarned: number;
  isFirst: boolean;
  timestamp: number;
}

export interface PlayerStats {
  userId: string;
  displayName: string;
  photoURL?: string;
  gameScore: number;
  totalAnswers: number;
  correctAnswers: number;
  currentStreak: number;
  bestStreak: number;
  preferredRole: GameRole;
  achievements: string[];
  roleScores: Record<GameRole, number>;
  lastActive: number;
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlockedAt?: number;
}

export const ACHIEVEMENTS: Record<string, Achievement> = {
  FIRST_BLOOD:    { id: 'FIRST_BLOOD',    name: 'First Blood',      description: 'Answer your first question correctly',       icon: '🎯' },
  THE_DISPATCH:   { id: 'THE_DISPATCH',   name: 'The Dispatch',     description: 'First to answer correctly on any round',      icon: '📡' },
  STREAK_3:       { id: 'STREAK_3',       name: 'On a Roll',        description: '3 correct answers in a row',                  icon: '🔥' },
  STREAK_5:       { id: 'STREAK_5',       name: 'Locked In',        description: '5 correct answers in a row',                  icon: '⚡' },
  STREAK_10:      { id: 'STREAK_10',      name: 'Legend',           description: '10 correct answers in a row',                 icon: '👑' },
  NIGHT_WATCH:    { id: 'NIGHT_WATCH',    name: 'Night Watch',      description: 'Answer 5 questions between 2am-5am',          icon: '🌙' },
  JACKPOT:        { id: 'JACKPOT',        name: 'Jackpot',          description: 'Win a CRITICAL threat jackpot round',         icon: '🎰' },
  PATTERN:        { id: 'PATTERN',        name: 'Pattern Recognition', description: 'Same location appeared 3 times tonight',   icon: '🔍' },
  ROOKIE:         { id: 'ROOKIE',         name: 'Rookie of the Year', description: 'Get 5 wrong in a row (we all start somewhere)', icon: '🎓' },
  CODE_444:       { id: 'CODE_444',       name: 'Code 4-4-4',       description: 'Answer correctly at 4:44am — spooky',         icon: '👻' },
  GHOST_PROTOCOL: { id: 'GHOST_PROTOCOL', name: 'Ghost Protocol',   description: 'You found the hidden mode',                  icon: '👁' },
  CENTURY:        { id: 'CENTURY',        name: 'Century',          description: '100 total correct answers',                   icon: '💯' },
  ARYA:           { id: 'ARYA',           name: "Arya's Watch",     description: "You heard the bark. She's always watching.",  icon: '🐕' },
  GHOST_WINS:     { id: 'GHOST_WINS',     name: 'Ghost',            description: 'Evade 5 times as Criminal without being caught', icon: '🕶️' },
  MOST_WANTED:    { id: 'MOST_WANTED',    name: 'Most Wanted',      description: 'Win 3 Criminal rounds in a row',             icon: '🎴' },
  GHOST_STREAK:   { id: 'GHOST_STREAK',   name: 'On The Run',       description: '5 Criminal wins in a row',                   icon: '🏃' },
};
